import { Box, Tab, Tabs } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch } from "redux/hooks";
import { resetPageModifiedFilter } from "../Filters/GlobalFilters.slice";

interface TabItem {
  label: string;
  content: React.ComponentType<any>;
  filtersToClear?: string[];
  getProps?: () => any;
}

interface UrlTabsProps {
  tabs: TabItem[];
  defaultTab?: number;
  tabParam?: "parentTab" | "childTab" | string;
  scrollTarget?: string | null;
  retainFilters?: boolean;
  isURLDriven?: boolean;
}

const UrlTabs: React.FC<UrlTabsProps> = ({
  tabs,
  defaultTab = 0,
  tabParam = "parentTab",
  retainFilters = false,
  isURLDriven = true,
  scrollTarget
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const prevValueRef = useRef<number>(defaultTab);
  const [internalValue, setInternalValue] = useState(defaultTab);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  /**
   * 🔹 Active tab index
   */
  const value = useMemo(() => {
    if (!isURLDriven) {
      return internalValue;
    }

    const label = searchParams.get(tabParam);
    const index = tabs.findIndex((t) => t.label === label);
    return index >= 0 ? index : defaultTab;
  }, [isURLDriven, internalValue, searchParams, tabs, defaultTab, tabParam]);

  /**
   * 🔹 Push default tab to URL (URL-driven only)
   */
  useEffect(() => {
    if (!isURLDriven || !tabs.length) return;

    if (!searchParams.get(tabParam)) {
      const params = new URLSearchParams(location.search);
      params.set(tabParam, tabs[defaultTab].label);

      navigate(`${location.pathname}?${params.toString()}`, {
        replace: true
      });
    }
  }, [isURLDriven, location.search, navigate, tabs, defaultTab, tabParam]);

  /**
   * 🔹 Handle tab change
   */
  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    const oldValue = prevValueRef.current;
    prevValueRef.current = newValue;

    const filtersToClear = tabs[oldValue]?.filtersToClear ?? [];
    if (!retainFilters && filtersToClear.length) {
      filtersToClear.forEach((pageName) => dispatch(resetPageModifiedFilter({ pageName })));
    }

    if (!isURLDriven) {
      setInternalValue(newValue);
      return;
    }

    const params = new URLSearchParams(location.search);

    // ✅ Reset child tab ONLY if it exists
    if (tabParam === "parentTab" && params.has("childTab")) {
      params.delete("childTab");
    }

    params.set(tabParam, tabs[newValue].label);

    navigate(`${location.pathname}?${params.toString()}`, {
      replace: true
    });
  };

  /**
   * 🔹 Render active tab content
   */
  const activeTab = tabs[value];
  const ActiveComponent = activeTab?.content;
  const activeProps = activeTab?.getProps?.();

  return (
    <Box width="100%">
      <Tabs
        value={value}
        onChange={handleChange}
        sx={{
          "& .MuiTabs-flexContainer": {
            px: { xs: 2, md: 0 }
          }
        }}
      >
        {tabs.map((tab) => (
          <Tab key={tab.label} label={tab.label} disableRipple />
        ))}
      </Tabs>

      <Box mt={2} {...(scrollTarget && { id: scrollTarget })} className="MuiTab-content">
        {ActiveComponent && <ActiveComponent {...activeProps} />}
      </Box>
    </Box>
  );
};

export default React.memo(UrlTabs);
